import config from "config/appConfig";

import type {
	ClubModel,
	CompetitionClubWithTeamsModel,
	CompetitionFieldWithReservationModel,
	CompetitionGroupModel,
	CompetitionModel,
	CompetitionPlayerModel,
	EntityRoles,
	FullTeamModel,
	MatchModel,
	PaginationTokenType,
	ScoutModel,
	TeamFiltersModelWithId,
	TournamentDivisionModel,
	TournamentStructModel
} from "shared/types";
import type { FieldInfo } from "shared/types/FacilityModel";
import type { WaiverModel } from "shared/types/Waiver";
import createStore from "utils/createStore";

import type { CompetitionHealthModel, CompetitionPlayersByTeamModel } from "../../types/CompetitionModel";

const localStorageKey = `${config.GLOBAL_CONSTANTS.LOCAL_STORAGE_PREFIX}${config.HOOK_STORAGE_KEYS.COMPETITION_DATA}`;

export interface Values {
	competitions: CompetitionModel[];
	loading: boolean;
	loadingOverview?: boolean;
	loadingInfo?: boolean;
	loadingTeams?: boolean;
	loadingGroups?: boolean;
	groups: CompetitionGroupModel[];
	submitting?: boolean;
	teams: FullTeamModel[];
	clubs: Partial<ClubModel>[];
	overview?: CompetitionModel;
	info?: CompetitionModel;
	loadingTeamFilters?: boolean;
	teamFilters?: TeamFiltersModelWithId;
	competitionContactSettingsModal?: boolean | CompetitionModel | string;
	competitionSettingsModal?: boolean | CompetitionModel | string;
	competitionSetupModal?: boolean | CompetitionModel | string;
	loadingReferees?: boolean;
	loadingCompetitionTeamStats: boolean;
	loadingCompetitionPlayerStats: boolean;
	loadingClubs: boolean;
	loadingMatches: boolean;
	matches: MatchModel[];
	userCompetitionRoles: EntityRoles[];
	nextListingMatchesToken: PaginationTokenType;
	prevListingMatchesToken: PaginationTokenType;
	loadingFollowedCompetitions: boolean;
	followedCompetitionsNextToken: PaginationTokenType;

	loadingFieldReservations: boolean;
	fieldReservations: CompetitionFieldWithReservationModel[];

	loadingCompetitionDivision: boolean;

	loadingCompetitionDivisions: boolean;
	divisions: TournamentDivisionModel[];

	loadingStructure: boolean;
	structure?: TournamentStructModel;

	deleting: boolean;

	loadingFavoriteCompetitionDivisions: boolean;
	favoriteCompetition?: Partial<CompetitionModel>;
	favoriteCompetitions: Partial<CompetitionModel>[];
	favoriteCompetitionDivisions: Partial<TournamentDivisionModel>[];
	favoriteCompetitionGroups: Partial<CompetitionGroupModel>[];

	loadingScouts: boolean;
	updatingScouts: boolean;
	scouts: ScoutModel[];

	updatingTeams: boolean;

	loadingPlayers: boolean;
	players: CompetitionPlayerModel[];

	loadingPlayersInTeams: boolean;
	teamPlayers: CompetitionPlayersByTeamModel[];
	prevListingTeamPlayersToken: PaginationTokenType;
	nextListingTeamPlayersToken: PaginationTokenType;

	loadingClubsWithTeams: boolean;
	clubsWithTeams: CompetitionClubWithTeamsModel[];

	loadingAudiences: boolean;
	audiences: {
		players: { id: string }[];
		teams: { id: string }[];
	};

	loadingFacilityFields: boolean;
	facilityFields: Partial<FieldInfo>[];

	loadingWaivers: boolean;
	waivers: WaiverModel[];
	nextListingWaiversToken: PaginationTokenType;

	loadingHealthDashboard: boolean;
	loadingMatchesHealthDashboard: boolean;
	healthDashboard?: CompetitionHealthModel;
}

export const initialState: Values = {
	competitions: [],
	loading: false,
	loadingOverview: false,
	loadingInfo: false,
	loadingTeams: false,
	loadingGroups: false,
	groups: [],
	submitting: false,
	teams: [],
	overview: undefined,
	info: undefined,
	loadingTeamFilters: false,
	teamFilters: undefined,
	competitionContactSettingsModal: false,
	competitionSettingsModal: false,
	competitionSetupModal: false,
	loadingReferees: false,
	loadingCompetitionTeamStats: false,
	loadingCompetitionPlayerStats: false,
	loadingClubs: false,
	loadingMatches: false,
	clubs: [],
	matches: [],
	userCompetitionRoles: [],
	nextListingMatchesToken: null,
	prevListingMatchesToken: null,
	loadingFollowedCompetitions: false,
	followedCompetitionsNextToken: null,

	loadingFieldReservations: false,
	fieldReservations: [],

	loadingCompetitionDivision: false,
	loadingCompetitionDivisions: false,
	divisions: [],

	loadingStructure: false,
	structure: undefined,

	deleting: false,

	loadingFavoriteCompetitionDivisions: false,
	favoriteCompetition: undefined,
	favoriteCompetitions: [],
	favoriteCompetitionDivisions: [],
	favoriteCompetitionGroups: [],

	loadingScouts: false,
	updatingScouts: false,
	scouts: [],

	updatingTeams: false,

	loadingPlayers: false,
	players: [],

	loadingPlayersInTeams: false,
	teamPlayers: [],
	prevListingTeamPlayersToken: null,
	nextListingTeamPlayersToken: null,

	loadingClubsWithTeams: false,
	clubsWithTeams: [],

	loadingAudiences: false,
	audiences: {
		players: [],
		teams: []
	},

	loadingFacilityFields: false,
	facilityFields: [],

	loadingWaivers: false,
	waivers: [],
	nextListingWaiversToken: null,

	loadingHealthDashboard: false,
	loadingMatchesHealthDashboard: false,
	healthDashboard: undefined
};

export default createStore<Values>({
	initialState,
	persistant: true,
	persistantName: localStorageKey
});
